import React, { useState, useEffect, memo } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import styles from '~/styles/NodeConfigurationDialog.module.css'

export interface NodeConfiguration {
  isAutomated: boolean
  modifierprompt: string
  aiEngine?: string
  documentStyle?: string
  markdownFormatVisible?: boolean // Add to match NodeDocument.tsx usage
  globalconstants?: boolean // Global constants inclusion flag
  analysisAndRecommendations?: boolean // Analysis & Recommendations flag
  [key: string]: any
}

export interface NodeConfigurationDialogProps {
  visible: boolean
  configuration: NodeConfiguration
  onSave: (config: NodeConfiguration) => void
  onHide: () => void
  additionalContent?: (
    config: NodeConfiguration,
    setConfig: (config: NodeConfiguration) => void
  ) => React.ReactNode // Pass config and setConfig
}

const NodeConfigurationDialog: React.FC<NodeConfigurationDialogProps> = ({
  visible,
  configuration,
  onSave,
  onHide,
  additionalContent,
}) => {
  const [config, setConfig] = useState<NodeConfiguration>(configuration)

  useEffect(() => {
    if (visible) {
      setConfig({
        ...configuration,
        isAutomated: configuration.isAutomated || false,
        modifierprompt: configuration.modifierprompt || '',
        aiEngine: configuration.aiEngine || 'openai-4o',
        documentStyle: configuration.documentStyle || '',
        markdownFormatVisible: configuration.markdownFormatVisible || false, // Include in initial state
      })
    }
  }, [visible, configuration])

  const handleSave = () => {
    onSave(config)
    onHide()
  }

  const aiEngineOptions = [
    {      label: 'xAI',
      items: [
        { label: 'Grok-2-Latest', value: 'xai-grok-2-latest' },
        { label: 'Grok-2-1212', value: 'xai-grok-2-1212' },
      ],
    },
    {
      label: 'OpenAI',
      items: [
        { label: '4o (great for most questions)', value: 'openai-4o' },
        { label: '4.5 (for writing and exploring ideas)', value: 'openai-4.5' },
        {
          label: '4o-mini (faster for most questions)',
          value: 'openai-4o-mini',
        },
        {
          label: 'o3-mini (fast at advanced reasoning)',
          value: 'openai-o3-mini',
        },
        {
          label: 'o3-mini-high (great for coding)',
          value: 'openai-o3-mini-high',
        },
      ],
    },
    {
      label: 'Anthropic',
      items: [
        { label: 'Claude 3.5 Sonnet', value: 'anthropic-claude-3.5-sonnet' },
        { label: 'Claude 3.7 Sonnet', value: 'anthropic-claude-3.7-sonnet' },
      ],
    },
    {
      label: 'Others',
      items: [
        { label: 'Mistral latest', value: 'mistral-latest' },
        { label: 'Internal DeepSeek R1', value: 'internal-deepseek-r1' },
      ],
    },
  ]

  return (
    <Dialog open={visible} onOpenChange={(open) => !open && onHide()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Configuration</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Node specific configuration options
        </DialogDescription>
        <div className="space-y-4">
          {configuration.modifierprompt !== undefined && (
            <div className="space-y-2">
              <Label htmlFor="modifierprompt">Modifier Prompt</Label>
              <Textarea
                id="modifierprompt"
                value={config.modifierprompt}
                onChange={(e) =>
                  setConfig({ ...config, modifierprompt: e.target.value })
                }
                rows={5}
                className="w-full resize-y"
              />
            </div>
          )}
          {configuration.documentStyle !== undefined && (
            <div className="space-y-2">
              <Label htmlFor="documentStyle">Document Style</Label>
              <Select
                value={config.documentStyle}
                onValueChange={(value) =>
                  setConfig({ ...config, documentStyle: value })
                }
              >
                <SelectTrigger id="documentStyle" className="w-full">
                  <SelectValue placeholder="Select a style" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="neutral">Neutral</SelectItem>
                  <SelectItem value="formal">Formal</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="conversational">Conversational</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}{' '}
          {configuration.isAutomated !== undefined && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isAutomated"
                checked={config.isAutomated}
                onCheckedChange={(checked) =>
                  setConfig({ ...config, isAutomated: checked === true })
                }
              />
              <Label htmlFor="isAutomated">Enable Automation</Label>
            </div>
          )}{' '}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="globalconstants"
              checked={config.globalconstants !== false}
              onCheckedChange={(checked) =>
                setConfig({ ...config, globalconstants: checked === true })
              }
            />
            <Label htmlFor="globalconstants">Include Global Constants</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="analysisAndRecommendations"
              checked={config.analysisAndRecommendations !== false}
              onCheckedChange={(checked) =>
                setConfig({
                  ...config,
                  analysisAndRecommendations: checked === true,
                })
              }
            />
            <Label htmlFor="analysisAndRecommendations">
              Analysis & Recommendations
            </Label>
          </div>
          <div className="flex flex-col space-y-2">
            <Label htmlFor="aiEngine" className="text-gray-700">
              AI Engine
            </Label>
            <Select
              value={config.aiEngine || 'openai-4o'}
              onValueChange={(value) =>
                setConfig({ ...config, aiEngine: value })
              }
            >
              <SelectTrigger id="aiEngine" className="w-full">
                <SelectValue placeholder="Select AI Engine" />
              </SelectTrigger>
              <SelectContent>
                {aiEngineOptions.map((group) => (
                  <React.Fragment key={group.label}>
                    <div className="px-2 py-1 font-bold text-gray-700 bg-gray-100">
                      {group.label}
                    </div>
                    {group.items.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </React.Fragment>
                ))}
              </SelectContent>
            </Select>
          </div>
          {additionalContent?.(config, setConfig)}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onHide}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default memo(NodeConfigurationDialog)
